import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ErrorsService } from '../../errors/errors-service/errors.service';
import { BrowserPlatformService } from '../browser-platform/browser-platform.service';
import { AnalyticsFeatures, AnalyticsUserState } from './analytics-keys';

type GtagWindow = Window & { gtag?: (...args: unknown[]) => void };

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private _errorsService = inject(ErrorsService);
    private _browser = inject(BrowserPlatformService);

    public sendEvent(category: string = '', action: string = ''): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        if (!win) {
            return;
        }

        try {
            if (win.gtag) {
                win.gtag('event', category, {
                    operation: action,
                    content_group: this._contentGroup(win),
                });
            }
        } catch (error) {
            if (environment.production) {
                this._errorsService.log(error);
            }
        }
    }

    public page_view(title: string) {
        const win = this._browser.nativeWindow as GtagWindow | null;
        if (win?.gtag) {
            win.gtag('event', 'page_view', {
                page_title: title,
                content_group: this._contentGroup(win),
            });
        }
    }

    /** Updates a user property; it rides every event sent from now on. */
    public setUserState<K extends keyof AnalyticsUserState>(key: K, value: AnalyticsUserState[K]): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        win?.gtag?.('set', 'user_properties', { [key]: value });
    }

    /** Reports an action on a feature (one event per call). */
    public featureUsed<K extends keyof AnalyticsFeatures>(feature: K, variant: AnalyticsFeatures[K]): void {
        const win = this._browser.nativeWindow as GtagWindow | null;
        win?.gtag?.('event', 'feature_use', {
            feature,
            variant,
            content_group: win ? this._contentGroup(win) : undefined,
        });
    }

    private _contentGroup(win: Window): string | undefined {
        return win.location.pathname.match(/^\/[^\/]*/)?.[0];
    }
}
